import React, { useEffect, useState } from "react";
import { OrderFormType } from "../../interfaces/orderFormType";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";
import {
  createOrderForm,
  determineOrderFormTypes,
} from "../../services/orderFormService";

interface AddRequiredFormProps {
  onClose: (message?: string) => void;
}

const AddRequiredForm: React.FC<AddRequiredFormProps> = ({ onClose }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [availableOrderFormTypes, setAvailableOrderFormTypes] = useState<
    OrderFormType[] | null
  >(null);
  const [selectedFormType, setSelectedFormType] =
    useState<OrderFormType | null>(null);

  const {
    client,
    order,
    referenceOrder,
    criminalOrder,
    subscriptionOrder,
    orderForms,
    setOrderForms,
    orderFormTypes,
  } = useViewOrderContext();

  useEffect(() => {
    const getDetails = async () => {
      try {
        setLoadingSpinner(true);

        const { forms } = determineOrderFormTypes(
          client,
          orderFormTypes,
          referenceOrder,
          criminalOrder,
          subscriptionOrder,
        );
        const existingFormTypeIDs = orderForms?.map(
          (oof: any) => oof.orderFormTypeID,
        );

        const availableAndUnusedForms = forms.filter(
          (form) => !existingFormTypeIDs?.includes(form.orderFormTypeID),
        );

        setAvailableOrderFormTypes(availableAndUnusedForms);
      } catch (error) {
        console.error("Error getting order form types:", error);
        setErrorMessage("Error getting order form types");
      } finally {
        setLoadingSpinner(false);
      }
    };

    getDetails();
  }, [
    client,
    referenceOrder,
    criminalOrder,
    subscriptionOrder,
    client?.denomination,
    order?.orderID,
    orderFormTypes,
    orderForms,
  ]);

  const handleAddForm = async () => {
    if (!selectedFormType) return;

    try {
      setLoadingSpinner(true);

      var newOrderForm = await createOrderForm(
        selectedFormType.orderFormTypeID,
        order.orderID,
      );
      setOrderForms((prevForms) => [...prevForms, newOrderForm]);
      onClose();
    } catch (error) {
      console.error("Error adding order form:", error);
      setErrorMessage("Failed to add the required form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Add a Required Order Form</h2>
          <div>Please choose a form type.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="form-group">
            <select
              id="formTypeSelect"
              className="form-control"
              value={selectedFormType?.description || ""}
              onChange={(e) => {
                const selected = availableOrderFormTypes?.find(
                  (type) => type.description === e.target.value,
                );
                setSelectedFormType(selected || null);
              }}
            >
              <option value="" disabled>
                -- Select Form Type --
              </option>
              {availableOrderFormTypes?.map((formType) => (
                <option
                  key={formType.orderFormTypeID}
                  value={formType.description}
                >
                  {formType.description}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button className="smbtn btn-grey" onClick={() => handleAddForm()}>
              Add Required Form
            </button>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default AddRequiredForm;
